import { useState, useEffect } from 'react';

// Import translation files at module level but wrap access safely
import ptTranslationsRaw from '@/locales/pt.json';
import enTranslationsRaw from '@/locales/en.json';

interface Translation {
  [key: string]: any;
}

interface UseTranslationReturn {
  t: (key: string, fallback?: string) => string;
  currentLanguage: string;
  setLanguage: (language: string) => void;
  isLoading: boolean;
}

// Wrap translations in a getter function to ensure safe access
const getTranslations = (): Record<string, Translation> => {
  try {
    return {
      pt: ptTranslationsRaw,
      en: enTranslationsRaw,
    };
  } catch (error) {
    console.error('[getTranslations] Error loading translations:', error);
    // Return minimal fallback
    return {
      pt: {},
      en: {},
    };
  }
};

// Get nested object value by path (e.g., "header.home")
const getNestedValue = (obj: any, path: string): string | undefined => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

export const useTranslation = (): UseTranslationReturn => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get language from localStorage or default to Portuguese
    // Check if localStorage is available (not in SSR)
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedLanguage = localStorage.getItem('preferred-language');
      return savedLanguage || 'pt';
    }
    return 'pt';
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Save language preference
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('preferred-language', currentLanguage);
    }
  }, [currentLanguage]);

  const t = (key: string, fallback?: string): string => {
    try {
      const allTranslations = getTranslations();
      const currentTranslations = allTranslations[currentLanguage] || allTranslations.pt;
      const translatedText = getNestedValue(currentTranslations, key);

      // Return the translation, fallback, or the key itself
      return translatedText || fallback || key;
    } catch (error) {
      console.error('[useTranslation] Error getting translation:', error);
      return fallback || key;
    }
  };

  const setLanguage = (language: string) => {
    try {
      const allTranslations = getTranslations();
      if (allTranslations[language]) {
        setCurrentLanguage(language);
      } else {
        console.warn(`Language "${language}" not supported. Falling back to Portuguese.`);
        setCurrentLanguage('pt');
      }
    } catch (error) {
      console.error('[useTranslation] Error setting language:', error);
      setCurrentLanguage('pt');
    }
  };

  return {
    t,
    currentLanguage,
    setLanguage,
    isLoading
  };
};